/**
 * CRM features InspireTel switches on per customer. Shared by the backend (enforcement) and the
 * UI (Admin Console switches, and hiding what is off), like modules.ts.
 */
export const CRM_FEATURES = [
  'callerNames',
  'clickToCall',
  'missedCallRecovery',
  'newCallerInbox',
  'callerLookup',
  'truecallerLookup',
  'screenPop',
  'phonebookSync',
  'spamBlocking',
  'insights',
  'weeklyDigest',
  'webhooks',
  'aiAssistant',
  'aiCallSummaries',
] as const;

export type CrmFeature = (typeof CRM_FEATURES)[number];
export type CrmFeatures = Record<CrmFeature, boolean>;

export type CrmFeatureGroup = 'Calls' | 'Contacts' | 'Insights' | 'Integrations' | 'AI';

export const CRM_FEATURE_DEFINITIONS: Array<{
  id: CrmFeature;
  label: string;
  description: string;
  group: CrmFeatureGroup;
  /** Needs something configured on the server before it can work. */
  requires?: 'transcription' | 'email' | 'lookup' | 'truecaller';
  /** InspireTel-only: hidden from customers and left out of the presets. */
  internal?: boolean;
}> = [
  {
    id: 'callerNames',
    label: 'Caller names everywhere',
    description: 'Show contact and company names instead of bare numbers on the dashboard, live calls and reports.',
    group: 'Calls',
  },
  {
    id: 'clickToCall',
    label: 'Click to call',
    description: "Dial a contact through the PBX: the user's own phone rings first, then it calls the contact.",
    group: 'Calls',
  },
  {
    id: 'missedCallRecovery',
    label: 'Missed-call recovery',
    description: 'Every missed call becomes a callback task with a deadline; it closes itself when the caller is reached.',
    group: 'Calls',
  },
  {
    id: 'screenPop',
    label: 'Incoming call pop-up',
    description: 'While the phone rings, show who is calling, their history, open tasks and last note.',
    group: 'Calls',
  },
  {
    id: 'newCallerInbox',
    label: 'New callers inbox',
    description: 'First-time callers wait in an inbox to be named, ignored or marked as spam.',
    group: 'Contacts',
  },
  {
    id: 'callerLookup',
    label: 'Unknown caller look-up',
    description:
      'Suggests a name for numbers that are not contacts yet, from Google Maps business listings and public web pages, in the pop-up and the New callers inbox. Each look-up sends the number to Google and a web-search service.',
    group: 'Contacts',
    requires: 'lookup',
  },
  {
    id: 'truecallerLookup',
    label: 'Open in Truecaller',
    description:
      'An "Open in Truecaller" button on unknown callers that opens the number on truecaller.com, to see who it is before saving them as a contact. Needs a free Truecaller account, signed in on the website.',
    group: 'Contacts',
  },
  {
    id: 'phonebookSync',
    label: 'PBX phone book sync',
    description: 'Contacts saved in the CRM are written to the PBX phone book, so desk phones show their names.',
    group: 'Contacts',
  },
  {
    id: 'spamBlocking',
    label: 'Spam blocking',
    description: 'Marking a caller as spam adds the number to the PBX blocked numbers.',
    group: 'Contacts',
  },
  {
    id: 'insights',
    label: 'Account insights',
    description: 'Flags customers who have gone quiet, call spikes, frequently missed callers, and suggests account owners.',
    group: 'Insights',
  },
  {
    id: 'weeklyDigest',
    label: 'Weekly summary',
    description: 'A weekly summary of calls, missed calls recovered, new callers and accounts to watch.',
    group: 'Insights',
    requires: 'email',
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    description: 'Send calls, missed calls, tasks and contacts to another system (Zapier, Make, HubSpot, Pipedrive...).',
    group: 'Integrations',
  },
  {
    id: 'aiAssistant',
    label: 'AI assistant for the CRM',
    description: 'Pre-call briefs on contacts, and CRM questions in Chat.',
    group: 'AI',
  },
  {
    id: 'aiCallSummaries',
    label: 'AI call summaries',
    description: 'Recorded calls are transcribed and summarised with action items on the contact. Needs recording consent.',
    group: 'AI',
    requires: 'transcription',
  },
];

/**
 * Starting points for selling the CRM in tiers. The Admin Console applies one and staff can
 * still switch single features after.
 */
export const CRM_FEATURE_PRESETS: Record<'off' | 'essentials' | 'professional' | 'complete', { label: string; features: CrmFeature[] }> = {
  off: { label: 'All off', features: [] },
  essentials: {
    label: 'Essentials',
    features: ['callerNames', 'clickToCall', 'missedCallRecovery', 'newCallerInbox', 'phonebookSync'],
  },
  professional: {
    label: 'Professional',
    features: [
      'callerNames',
      'clickToCall',
      'missedCallRecovery',
      'newCallerInbox',
      'phonebookSync',
      'screenPop',
      'spamBlocking',
      'insights',
      'weeklyDigest',
      'webhooks',
    ],
  },
  complete: {
    label: 'Complete (with AI)',
    features: CRM_FEATURES.filter((id) => !CRM_FEATURE_DEFINITIONS.find((feature) => feature.id === id)?.internal),
  },
};

/**
 * Existing customers keep what they had before switches existed: contacts saved in the CRM were
 * always written to the PBX phone book. Everything new starts off.
 */
export function defaultCrmFeatures(): CrmFeatures {
  return Object.fromEntries(CRM_FEATURES.map((id) => [id, id === 'phonebookSync'])) as CrmFeatures;
}

export function normalizeCrmFeatures(input?: Partial<Record<string, boolean>> | null): CrmFeatures {
  const defaults = defaultCrmFeatures();
  return Object.fromEntries(
    CRM_FEATURES.map((id) => [id, typeof input?.[id] === 'boolean' ? Boolean(input[id]) : defaults[id]])
  ) as CrmFeatures;
}

export const DEFAULT_CALLBACK_SLA_MINUTES = 30;
export const DEFAULT_VIP_SLA_MINUTES = 10;
export const DEFAULT_AI_MIN_TALK_SECONDS = 45;
export const DEFAULT_AI_DAILY_LIMIT = 100;
export const DEFAULT_TRANSCRIPT_RETENTION_DAYS = 365;
export const DEFAULT_LOOKUP_DAILY_LIMIT = 100;
export const DEFAULT_TRUECALLER_DAILY_LIMIT = 50;
